import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import GenericTable from 'components/display/Table/Table';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import {
  DropdownButton,
  useActionMenu
} from 'components/navigation/Dropdown/GenericDropdownButton';
import { useDialog } from 'hooks/useDialog';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { BrokerForm } from 'back-office/features/brokers-view/form/BrokerForm';
import { useBrokerForm } from 'back-office/features/brokers-view/hooks/useBrokerForm';
import { GestionnaireForm } from 'back-office/features/gestionnaires-view/form/GestionnaireForm';
import { useGestionnaireForm } from 'back-office/features/gestionnaires-view/hooks/useGestionnaireForm';
import { MiaForm } from 'back-office/features/mias-view/form/MiaForm';
import { useMiaForm } from 'back-office/features/mias-view/hooks/useMiaForm';
import { PimForm } from 'back-office/features/pims-view/form/PimForm';
import { usePimForm } from 'back-office/features/pims-view/hooks/usePimForm';
import { CompagnieForm } from 'back-office/features/compagnies-view/form/CompagnieForm';
import { useCompagnieForm } from 'back-office/features/compagnies-view/hooks/useCompagnieForm';

import {
  PersonneMoraleListItemDto,
  searchPersonnesMorales
} from './api/personnesMoralesApi';
import {
  avatarClasses,
  avatarInitials
} from 'back-office/shared/components/collaborators/collaboratorAvatar';
import {
  NewPersonneMoraleTypeModal,
  PersonneMoraleType
} from './components/NewPersonneMoraleTypeModal';
import { formatSiret } from 'shared/utils/formatSiret';

const DEFAULT_PAGE_SIZE = 15;

/** Badge d'identité des sous-natures (variants d'une nature de PM). */
const SOUS_NATURE_BADGES: Record<string, { label: string; className: string }> =
  {
    GRO: { label: 'Grossiste', className: 'bg-violet-100 text-violet-700' },
    MUT: { label: 'Mutuelle', className: 'bg-emerald-100 text-emerald-700' },
    REA: { label: 'Réassureur', className: 'bg-sky-100 text-sky-700' }
  };

const formatCurrency = (amount?: number) => {
  if (amount == null) return '0 €';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (date?: string | null) => {
  if (!date) return 'Jamais';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatPhone = (phone?: string) => {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(?=\d)/g, '$1 ');
  }
  return phone;
};

interface PersonnesMoralesTableProps {
  /** Restreint la liste à une nature de PM (ex. CIE pour la vue Compagnies). */
  nature?: string;
}

export const PersonnesMoralesTable: React.FC<PersonnesMoralesTableProps> = ({
  nature
}) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PersonneMoraleListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const response = await searchPersonnesMorales({
        pageNumber: page,
        pageSize,
        searchString: filter || undefined,
        nature
      });
      setRows(response.items ?? []);
      setTotalCount(response.totalCount ?? 0);
    } catch {
      toasterService.showToast({
        message: 'Erreur de chargement des personnes morales.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter, nature]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const goToDetails = useCallback(
    // Vue filtrée (ex. Compagnies) : la fiche vit sous la route personnes-morales.
    (pm: PersonneMoraleListItemDto) =>
      navigate(
        nature ? `/back-office/acteurs/personnes-morales/${pm.id}` : pm.id
      ),
    [navigate, nature]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize]
  );

  const { createMenuItem } = useActionMenu();

  const createMenuItems = useCallback(
    (_pm: PersonneMoraleListItemDto) => [
      createMenuItem(
        'edit',
        'Modifier',
        () => {
          /* TODO */
        },
        { icon: 'edit' }
      )
    ],
    [createMenuItem]
  );

  const { openBlankDialog, closeDialog } = useDialog();
  const { openPanel } = usePanel();
  const { createBroker } = useBrokerForm();
  const { createGestionnaire } = useGestionnaireForm();
  const { createMia } = useMiaForm();
  const { createPim } = usePimForm();
  const { createCompagnie } = useCompagnieForm();

  const openBrokerForm = useCallback(() => {
    openPanel(
      { title: 'Ajouter un courtier', size: Sizes['4xl'] },
      <BrokerForm onSubmit={createBroker} onSuccess={() => fetchPage()} />
    );
  }, [openPanel, createBroker, fetchPage]);

  const openGestionnaireForm = useCallback(() => {
    openPanel(
      { title: 'Ajouter un gestionnaire', size: Sizes['4xl'] },
      <GestionnaireForm
        onSubmit={createGestionnaire}
        onSuccess={() => fetchPage()}
      />
    );
  }, [openPanel, createGestionnaire, fetchPage]);

  const openMiaForm = useCallback(() => {
    openPanel(
      { title: 'Ajouter un MIA', size: Sizes['4xl'] },
      <MiaForm onSubmit={createMia} onSuccess={() => fetchPage()} />
    );
  }, [openPanel, createMia, fetchPage]);

  const openPimForm = useCallback(() => {
    openPanel(
      { title: "Ajouter un professionnel de l'immobilier", size: Sizes['4xl'] },
      <PimForm onSubmit={createPim} onSuccess={() => fetchPage()} />
    );
  }, [openPanel, createPim, fetchPage]);

  const openCompagnieForm = useCallback(() => {
    openPanel(
      { title: 'Ajouter une compagnie', size: Sizes['4xl'] },
      <CompagnieForm onSubmit={createCompagnie} onSuccess={() => fetchPage()} />
    );
  }, [openPanel, createCompagnie, fetchPage]);

  const handleTypeSelect = useCallback(
    (type: PersonneMoraleType) => {
      if (type === 'courtier') {
        openBrokerForm();
      } else if (type === 'gestionnaire') {
        openGestionnaireForm();
      } else if (type === 'mia') {
        openMiaForm();
      } else if (type === 'pim') {
        openPimForm();
      } else if (type === 'compagnie') {
        openCompagnieForm();
      }
    },
    [
      openBrokerForm,
      openGestionnaireForm,
      openMiaForm,
      openPimForm,
      openCompagnieForm
    ]
  );

  const openCreateModal = useCallback(() => {
    // Vue restreinte à une nature (ex. Compagnies) : on ouvre directement le
    // formulaire de cette nature, sans passer par le choix du type.
    if (nature === 'CIE') {
      openCompagnieForm();
      return;
    }
    openBlankDialog(
      { size: Sizes['5xl'] },
      <NewPersonneMoraleTypeModal
        onClose={closeDialog}
        onSelect={handleTypeSelect}
      />
    );
  }, [
    nature,
    openCompagnieForm,
    openBlankDialog,
    closeDialog,
    handleTypeSelect
  ]);

  return (
    <GenericTable<PersonneMoraleListItemDto>
      config={{
        loading,
        skeletonRowCount: 10,
        data: rows,
        columns: [
          {
            key: 'designation',
            label: 'Entreprise',
            sortable: true,
            render: (_value, row) => (
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-[11px] font-bold text-primary">
                  {row.designation.slice(0, 2).toUpperCase()}
                </span>
                <span className="truncate font-semibold text-gray-900">
                  {row.designation}
                </span>
                {row.sousNature && SOUS_NATURE_BADGES[row.sousNature] && (
                  <span
                    className={`flex-shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      SOUS_NATURE_BADGES[row.sousNature].className
                    }`}
                  >
                    {SOUS_NATURE_BADGES[row.sousNature].label}
                  </span>
                )}
                {row.raisonJuridique?.libelle && (
                  <span className="flex-shrink-0 truncate text-xs text-gray-400">
                    {row.raisonJuridique.libelle}
                  </span>
                )}
              </div>
            )
          },
          {
            key: 'numeroSiret',
            label: 'SIRET',
            render: (_value, row) => (
              <span className="whitespace-nowrap font-mono text-xs text-gray-700">
                {formatSiret(row.numeroSiret) || '—'}
              </span>
            )
          },
          {
            key: 'collaborators',
            label: 'Gérant principal',
            render: (_value, row) => {
              const principal = row.collaborators?.find(m => m.isPrincipal);
              if (!principal) {
                return (
                  <span className="text-sm text-gray-400">Aucun collaborateur</span>
                );
              }
              const name =
                `${principal.firstName ?? ''} ${
                  principal.lastName ?? ''
                }`.trim() || principal.email;
              return (
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${avatarClasses(
                      principal.abpUserId
                    )}`}
                  >
                    {avatarInitials(
                      principal.firstName,
                      principal.lastName,
                      principal.email
                    )}
                  </span>
                  <span className="truncate text-sm text-gray-900">{name}</span>
                </div>
              );
            }
          },
          {
            key: 'telephone',
            label: 'Téléphone',
            render: (_value, row) => (
              <span className="whitespace-nowrap font-mono text-xs text-gray-700">
                {formatPhone(row.telephone)}
              </span>
            )
          },
          {
            key: 'adresse',
            label: 'Localisation',
            render: (_value, row) => {
              const addr = row.adresse;
              if (!addr?.city) {
                return <span className="text-sm text-gray-400">—</span>;
              }
              return (
                <span className="truncate text-sm text-gray-700">
                  {addr.city}
                  {addr.zipCode ? ` (${addr.zipCode})` : ''}
                </span>
              );
            }
          },
          {
            key: 'capital',
            label: 'Capital',
            align: 'right',
            render: (_value, row) => (
              <span className="whitespace-nowrap text-sm text-gray-700">
                {row.capital ? formatCurrency(row.capital) : '—'}
              </span>
            )
          },
          {
            key: 'isValidOrias',
            label: 'Statut',
            render: (_value, row) => {
              const principal = row.collaborators?.find(m => m.isPrincipal);
              const actif = !!principal?.isActive;
              return (
                <span
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
                    actif
                      ? 'bg-green-50 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      actif ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  />
                  {actif ? 'Actif' : 'Inactif'}
                </span>
              );
            }
          },
          {
            key: 'lastConnectionDate' as keyof PersonneMoraleListItemDto,
            label: 'Connexion',
            sortable: false,
            render: (_value, row) => {
              const principal = row.collaborators?.find(m => m.isPrincipal);
              const date = principal?.lastConnectionDate;
              return (
                <span
                  className={`whitespace-nowrap text-sm ${
                    date ? 'text-gray-700' : 'text-gray-400'
                  }`}
                >
                  {formatDate(date)}
                </span>
              );
            }
          }
        ],
        actions: {
          create: {
            label:
              nature === 'CIE'
                ? 'Ajouter une compagnie'
                : 'Ajouter une personne morale',
            onClick: openCreateModal
          },
          row: [
            {
              label: 'seeMore',
              render: (pm: PersonneMoraleListItemDto, index: number) => (
                <Button
                  key={index}
                  color="slate"
                  round
                  onClick={() => goToDetails(pm)}
                >
                  <EyeIcon />
                </Button>
              )
            },
            {
              label: 'moreMenu',
              render: (pm: PersonneMoraleListItemDto, index: number) => (
                <DropdownButton
                  key={index}
                  width="w-56"
                  anchor="bottom"
                  items={createMenuItems(pm)}
                />
              )
            }
          ]
        },
        pagination: {
          enabled: true,
          pageSize,
          currentPage: page,
          totalPages,
          totalItems: totalCount,
          showInfo: true,
          pageSizeOptions: [10, 15, 25, 50],
          onPageChange: setPage,
          onPageSizeChange: size => {
            setPageSize(size);
            setPage(1);
          }
        },
        filters: {
          enabled: true,
          searchable: true,
          searchString: filter,
          searchPlaceholder:
            'Rechercher une personne morale (désignation, SIRET...)',
          onSearch: term => {
            setPage(1);
            setFilter(term);
          }
        }
      }}
    />
  );
};

export default PersonnesMoralesTable;
